import { OnInit, OnDestroy, Component, Input, EventEmitter, Output, Inject } from '@angular/core';
import { ReplaySubject, Subject, race, timer, takeUntil, take, concatMap } from 'rxjs';
import { Convert, USE_VERIFICATION_UPPER_MRZ, WS_SERVICE_FACTORY } from 'types';
import {
  PassportReaderService,
  DataGroup2,
  PassportStatusSOD,
  DataGroup,
  ActivationError,
  ActivationState,
} from '../../service/passport-reader.service';
import { PortraitService } from '../../service/portrait.service';
import { AppContext } from '../../base/control/context';
import { PassportData, MRZhandler, ActivationSetting, VerificationSetting } from '../pass-verify/passportData';
import { FingerprintResponseData, BioInfoTemplate } from '../../service/hw-service-data';
import { FingerprintsVerifyResult } from './pass.fp.verify.component';
import { FingerLocalization } from './fingerprint-context';
import { HW_SERVICE_MOCK } from '../../service/hw-service-model';
import { HWServiceConfig } from '../../service/hw-service-config';
import { portraitServiceFactory } from '../../service/hw-service-factory';
import { Md5 } from 'ts-md5/dist/esm/md5';

@Component({
  selector: 'i-pass-scan-chip',
  templateUrl: './pass.scan.chip.component.html',
  styleUrls: ['./pass.scan.chip.component.scss'],
  providers: [
    { provide: PortraitService, useFactory: portraitServiceFactory, deps: [HW_SERVICE_MOCK, HWServiceConfig, WS_SERVICE_FACTORY] },
  ],
})
export class PassScanChipComponent implements OnInit, OnDestroy {
  @Input() mrz2: string;
  @Input() activationSettings: ActivationSetting;
  @Input() verificationSettings: VerificationSetting;
  @Output() MRZDataTablet = new EventEmitter<PassportData>();
  @Output() ChipData = new EventEmitter<PassportData>();
  @Output() ScanChipMessages = new EventEmitter<ScanChipMessage>();
  @Output() PassportStatus = new EventEmitter<PassportStatusSOD>();
  @Output() onMissingDg3 = new EventEmitter<void>();
  @Output() onMissingEAC = new EventEmitter<void>();
  @Output() onMissingFingerprints = new EventEmitter<void>();
  @Output() onSkippedFingerprints = new EventEmitter<void>();
  @Output() restartRequier = new EventEmitter<void>();
  @Input() EAC: boolean = false;

  scanFingerprints: boolean = false;
  fingerprintsData: BioInfoTemplate[];
  fingerprintsFinished = new Subject<FingerprintsVerifyResult>();
  fingerprintsAborted = new Subject<FingerprintsVerifyResult>();

  private readerId: number = 0;
  private dataPass = new PassportData();
  private dead: ReplaySubject<void> = new ReplaySubject();
  private activationDelayMilliseconds = 1000;
  private connectToChipDelayMilliseconds = 1000;
  private connectToChipAttempts = 30;

  constructor(
    public appContext: AppContext,
    private passportReaderService: PassportReaderService,
    private portraitService: PortraitService,
    @Inject(USE_VERIFICATION_UPPER_MRZ) private useVerificationUpperMRZ: boolean,
  ) {}

  ngOnInit() {
    this.startScaning();
  }

  startScaning() {
    this.passportReaderService.setActivationState(ActivationState.Process);
    this.ScanChipMessages.emit(new ScanChipMessage(false, 'Приложите паспорт к считывателю...'));
    this.passportReaderService.connect();
    this.portraitService.connect();
    this.connectToChip();
  }

  private connectToChip(connectionAttempts = 0) {
    console.log('%c Activation required flag:', 'background:#efefef;padding:4px', !!this.activationSettings.required);
    setTimeout(() => {
      this.passportReaderService
        .EnumPassportReaders()
        .pipe(
          concatMap((readers) => {
            const goodReaders = [...readers.entries()].filter((y) => !y[1] || !y[1].toLowerCase().includes('rutoken'));
            if (goodReaders.length) {
              this.readerId = goodReaders[0][0];
            }
            return this.passportReaderService.ConnectPassportReader(this.readerId);
          }),
          takeUntil(this.dead),
        )
        .subscribe(
          (r) => {
            this.activationSettings.required ? this.choiseReaderProcces() : this.PassBAC();
          },
          (e) => {
            if (e == 'openReader' && connectionAttempts < this.connectToChipAttempts) {
              this.connectToChip(connectionAttempts + 1);
            } else {
              this.callError('PASSPORT.VERIFICATION.ERROR.CONNECT', e);
              this.handleActivationError(e);
            }
          },
        );
    }, this.connectToChipDelayMilliseconds);
  }

  private doesStatementHaveMrz(): boolean {
    if (!this.activationSettings?.mrz2) {
      this.passportReaderService.setActivationState(ActivationState.CheckStatementMrzFailed);
      this.ScanChipMessages.emit(new ScanChipMessage(false, 'В завлении отсутствует MRZ'));
      return false;
    } else {
      return true;
    }
  }

  private doesStatementHaveActivationKey(): boolean {
    if (!this.activationSettings?.code) {
      this.passportReaderService.setActivationState(ActivationState.CheckStatementActivationKeyFailed);
      this.ScanChipMessages.emit(new ScanChipMessage(false, 'Не обнаружен код активации'));
      return false;
    } else {
      return true;
    }
  }

  private checkStatementSelection$() {
    return this.passportReaderService.GetCheckBAC(this.activationSettings.mrz2!).pipe(takeUntil(this.dead));
  }

  private choiseReaderProcces() {
    this.passportReaderService.setActivationState(ActivationState.Process);
    this.ScanChipMessages.emit(new ScanChipMessage(false, 'Проверка активации паспорта...'));
    this.passportReaderService
      .CheckPassportActivation(this.readerId)
      .pipe(takeUntil(this.dead))
      .subscribe(
        (response) => {
          if (response.activated) {
            this.handleActivationNotRequired();
          } else {
            this.handleActivationRequired();
          }
        },
        (error) => {
          this.handleActivationError(error);
        },
      );
  }

  private handleActivationNotRequired() {
    this.ScanChipMessages.emit(new ScanChipMessage(false, 'Паспорт уже активирован'));
    this.passportReaderService.setActivationState(ActivationState.NotRequired);
    timer(this.activationDelayMilliseconds).subscribe(() => {
      this.handleSingleMode();
    });
  }

  private handleActivationRequired() {
    if (!this.doesStatementHaveMrz() || !this.doesStatementHaveActivationKey()) {
      return;
    }
    this.checkStatementSelection$().subscribe({
      next: () => {
        this.handleActivationAttempt();
      },
      error: () => {
        this.passportReaderService.setActivationState(ActivationState.CheckStatementSelectionFailed);
        this.ScanChipMessages.emit(new ScanChipMessage(false, 'Паспорт не соответствует выбранному заявлению'));
      },
    });
  }

  private handleActivationAttempt() {
    this.ScanChipMessages.emit(new ScanChipMessage(false, 'Активация паспорта...'));
    this.passportReaderService.setActivationState(ActivationState.Attempt);
    this.passportReaderService
      .activatePassportByCode(this.readerId, this.activationSettings.code as string)
      .pipe(takeUntil(this.dead))
      .subscribe({
        next: () => {
          this.handleActivationSuccess();
        },
        error: (error) => {
          this.handleActivationError(error);
        },
      });
  }

  private handleActivationSuccess() {
    this.ScanChipMessages.emit(new ScanChipMessage(false, 'Паспорт активирован'));
    this.passportReaderService.setActivationState(ActivationState.Activated);
    timer(this.activationDelayMilliseconds).subscribe(() => {
      this.handleSingleMode();
    });
  }

  private handleActivationError(error: ActivationError) {
    if (error.tries_left !== undefined) {
      this.passportReaderService.setActivationState(ActivationState.ErrorCritical);
    } else {
      this.passportReaderService.setActivationState(ActivationState.Error);
    }
    this.callError('PASSPORT.VERIFICATION.ERROR.ACTIVATION', error.message);
  }

  private handleSingleMode() {
    this.activationSettings.singleMode ? this.fullDisconnect() : this.PassBAC();
  }

  private PassBAC() {
    this.ScanChipMessages.emit(new ScanChipMessage(false, 'Проверка безопасности BAC...'));
    this.passportReaderService
      .GetCheckBAC(this.mrz2)
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => this.ReadDG1(),
        (e) => this.callError('PASSPORT.VERIFICATION.ERROR.AC', e),
      );
  }

  private ReadDG1() {
    this.ScanChipMessages.emit(new ScanChipMessage(false, 'Чтение данных...'));
    this.passportReaderService
      .GetPassData()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          if (typeof r === 'number') {
            this.ScanChipMessages.emit(new ScanChipMessage(false, 'Прогресс считывания DG1: ' + r + '%'));
          } else {
            this.dataPass.photo = null;
            MRZhandler(this.appContext, this.dataPass, r.data.mrz);
            if (!this.useVerificationUpperMRZ) {
              this.MRZDataTablet.emit(this.dataPass);
            }
            this.ReadDG2();
          }
        },
        (e) => this.callError('PASSPORT.VERIFICATION.ERROR.READ', e),
      );
  }

  private ReadDG2() {
    this.ScanChipMessages.emit(new ScanChipMessage(false, 'Чтение данных...'));
    this.passportReaderService
      .GetPassPhoto()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          if (typeof r === 'number') {
            this.ScanChipMessages.emit(new ScanChipMessage(false, 'Прогресс считывания DG2: ' + r + '%'));
          } else {
            if (
              r.data['bio-info-templates'][0]['bio-data']['face-info']['face-images'][0]['image-data-type'] === 'jpeg2000' ||
              r.data['bio-info-templates'][0]['bio-data']['face-info']['face-images'][0]['image-data-type'] === 'jpeg'
            ) {
              const rawChipPhoto = r.data['bio-info-templates'][0]['bio-data']['face-info']['face-images'][0].data;
              const md5 = new Md5();
              const md5Res = md5.appendByteArray(Convert.toByteArray(rawChipPhoto)).end() as string;
              this.dataPass.photoHash = md5Res ? md5Res : '';
              console.log(
                '%c >>>> ReadDG2 (rawChipPhoto hash md5):',
                'background:#efefef;padding:4px',
                'dataPass.photoHash:',
                this.dataPass.photoHash,
                'md5Res:',
                md5Res,
              ); // Убрать логирование после отладки в ПСФ
              this.convertOperations(r);
            } else {
              this.dataPass.photo =
                'data:image/png;base64,' + r.data['bio-info-templates'][0]['bio-data']['face-info']['face-images'][0].data;
              this.ReadSODbyICAO();
            }
          }
        },
        (e) => this.callError('PASSPORT.VERIFICATION.ERROR.READ', e),
      );
  }

  private convertOperations(photo: DataGroup2) {
    const blob = Convert.toBlob(photo.data['bio-info-templates'][0]['bio-data']['face-info']['face-images'][0].data);
    this.convertOperationsUpload(blob);
  }

  private convertOperationsUpload(blob: Blob) {
    setTimeout(() => {
      this.portraitService
        .upload(blob)
        .pipe(takeUntil(this.dead))
        .subscribe(
          (r) => this.convertOperationsConvert(),
          (e) => this.callError('PASSPORT.VERIFICATION.ERROR.CONVERT', e),
        );
    }, 100);
  }

  private convertOperationsConvert() {
    setTimeout(() => {
      this.portraitService
        .convertJpg2()
        .pipe(takeUntil(this.dead))
        .subscribe(
          (r) => this.convertOperationsDownload(),
          (e) => this.callError('PASSPORT.VERIFICATION.ERROR.CONVERT', e),
        );
    }, 100);
  }

  private convertOperationsDownload() {
    setTimeout(() => {
      this.portraitService
        .download(false)
        .pipe(takeUntil(this.dead))
        .subscribe(
          (r) => this.downloadComplete(r),
          (e) => this.callError('PASSPORT.VERIFICATION.ERROR.CONVERT', e),
        );
    }, 300);
  }

  private downloadComplete(blob: Blob) {
    Convert.toBase64$(blob).subscribe((r) => {
      this.dataPass.photo = Convert.wrapBase64ToDataUrl(r);
      this.ChipData.emit(this.dataPass);
      this.ReadSODbyICAO();
    });
  }

  private ReadSODbyICAO() {
    this.ScanChipMessages.emit(new ScanChipMessage(false, 'Чтение данных...'));
    this.passportReaderService
      .GetPassSODbyICAO()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          if (typeof r === 'number') {
            this.ScanChipMessages.emit(new ScanChipMessage(false, 'Прогресс считывания ICAO SOD: ' + r + '%'));
          } else {
            this.ReadSODbyGOST();
          }
        },
        (e) => {
          this.callError('PASSPORT.VERIFICATION.ERROR.SOD.ICAO', e);
        },
      );
  }

  private ReadSODbyGOST() {
    this.ScanChipMessages.emit(new ScanChipMessage(false, 'Чтение данных...'));
    this.passportReaderService
      .GetPassSODbyGost()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          if (typeof r === 'number') {
            this.ScanChipMessages.emit(new ScanChipMessage(false, 'Прогресс считывания данных GOST SOD: ' + r + '%'));
          } else {
            if (this.EAC) {
              this.PassEACbyGOST();
            } else {
              this.Verify();
            }
          }
        },
        (e) => this.callError('PASSPORT.VERIFICATION.ERROR.SOD.GOST', e),
      );
  }

  private PassEACbyGOST() {
    this.ScanChipMessages.emit(new ScanChipMessage(false, 'Проверка безопасности EAC GOST...'));
    this.passportReaderService
      .GetPassHeader()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          if (typeof r !== 'number') {
            if (r.data.tags.indexOf('DG3') !== -1) {
              this.CheckTerminalKeys();
            } else {
              this.onMissingDg3.emit();
              this.Verify();
            }
          }
        },
        (e) => this.callError('PASSPORT.VERIFICATION.ERROR.AC', e),
      );
  }

  private UploadCVCALinks() {
    this.ScanChipMessages.emit(new ScanChipMessage(false, 'Загрузка link-сертификатов...'));
    this.passportReaderService
      .UploadCVCALinks()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => this.PassEACbyGOSTFinal(),
        (e) => this.callError('PASSPORT.VERIFICATION.ERROR.UPLOAD.CVCA.LINKS', e),
      );
  }

  private CheckTerminalKeys() {
    this.passportReaderService
      .CheckTerminalKeys()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          if (r) {
            this.UploadCVCALinks();
          } else {
            this.onMissingEAC.emit();
            this.Verify();
          }
        },
        (e) => this.callError('PASSPORT.VERIFICATION.ERROR.AC', e),
      );
  }

  private PassEACbyGOSTFinal() {
    this.passportReaderService
      .GetCheckEACbyGOST()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => this.ReadDG3(),
        (e) => {
          this.callError('PASSPORT.VERIFICATION.ERROR.AC', e, false);
          this.Verify();
        },
      );
  }

  private ReadDG3() {
    this.ScanChipMessages.emit(new ScanChipMessage(false, 'Чтение данных...'));
    this.passportReaderService
      .GetPassFingerprints()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          if (typeof r === 'number') {
            this.ScanChipMessages.emit(new ScanChipMessage(false, 'Прогресс считывания DG3: ' + r + '%'));
          } else {
            this.VerifyFingerprints(r);
          }
        },
        (e) => this.callError('PASSPORT.VERIFICATION.ERROR.READ', e),
      );
  }

  private VerifyFingerprints(dataGroup: DataGroup<FingerprintResponseData>): void {
    if (!this.verificationSettings.useCheckFingerprints) {
      this.Verify();
    }
    if (dataGroup.data['bio-info-templates'].length > 0) {
      this.fingerprintsData = dataGroup.data['bio-info-templates'];
      this.scanFingerprints = true;
      race(this.fingerprintsAborted.asObservable(), this.fingerprintsFinished.asObservable())
        .pipe(take(1), takeUntil(this.dead))
        .subscribe((r) => {
          if (!r.valid) {
            r.invalidFingers.forEach((finger) => {
              if (finger.scanState === 2) {
                this.callError(
                  'PASSPORT.VERIFICATION.ERROR.FINGERPRINTS',
                  r.invalidFingers
                    .map((x) => {
                      const [hand, finger] = x.data.position.split(' ');
                      return FingerLocalization.fingerName(finger) + ' ' + FingerLocalization.handName(hand) + ' руки';
                    })
                    .join(', '),
                );
              } else {
                this.ScanChipMessages.emit(new ScanChipMessage(false, 'Пропущена процедура проверки отпечатков'));
                setTimeout(() => {
                  this.onSkippedFingerprints.emit();
                  this.Verify();
                }, 2000);
              }
            });
          } else {
            this.Verify();
          }
          this.scanFingerprints = false;
        });
    } else {
      this.onMissingFingerprints.emit();
      this.Verify();
    }
  }

  private Verify() {
    this.passportReaderService
      .VerifyPass()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          if (r instanceof PassportStatusSOD) {
            this.PassportStatus.emit(r);
          }
        },
        (e) => this.callError('PASSPORT.VERIFICATION.ERROR.VERIFY', e),
      );
  }

  private callError(x: string, y: any, disconnect: boolean = true) {
    this.appContext.getMessageSink()('ERROR', x);
    const serviceResponse = y.match('^([a-zA-Z]+):([0-9]+)$');
    if (serviceResponse != null) {
      y =
        this.appContext.getI18NString('PASSPORT.SERVICE.ERROR.CODE.' + serviceResponse[2]) +
        ' (' +
        this.appContext.getI18NString('PASSPORT.SERVICE.METHOD.' + serviceResponse[1]) +
        ')';
    }
    this.ScanChipMessages.emit(new ScanChipMessage(true, this.appContext.getI18NString(x) + ': ' + y));
    if (disconnect) {
      this.passportReaderService.disconnect();
    }
  }

  ngOnDestroy() {
    this.fullDisconnect();
  }

  private fullDisconnect() {
    this.portraitService.disconnect();
    this.passportReaderService.disconnect();
    this.dead.next();
    this.dead.complete();
  }
}

export class ScanChipMessage {
  public constructor(error: boolean, message: string) {
    this.error = error;
    this.message = message;
  }
  public error: boolean;
  public message: string;
}
